import { Reading } from '../types';
import { supabase } from '../config/supabase';
import { v4 as uuidv4 } from 'uuid';
import { useAuthStore } from '../store/authStore';
import imageCompression from 'browser-image-compression';

// Set timeout to 60 seconds
const UPLOAD_TIMEOUT = 60000;
const INSERT_TIMEOUT = 30000; // 30 seconds for insert operations

// Helper function to map Supabase data to our frontend type
function mapReadingFromSupabase(data: any): Reading {
  return {
    id: data.id,
    createdAt: data.created_at,
    subject: data.subject,
    question: data.question || '',
    context: data.context,
    spreadType: data.spread_type as SpreadType,
    cards: data.cards || [],
    userInterpretation: data.user_interpretation || '',
    aiInterpretation: data.ai_interpretation,
    themes: data.themes || [],
    advice: data.advice,
    notes: data.notes || '',
    imageUrl: data.image_url,
    readingTitle: data.reading_title,
    is_virtual: data.is_virtual || false,
    user_id: data.user_id
  };
}

// Helper function to map our frontend type to Supabase data
function mapReadingToSupabase(reading: Omit<Reading, 'id' | 'createdAt'>) {
  return {
    subject: reading.subject,
    question: reading.question || '',
    context: reading.context,
    spread_type: reading.spreadType,
    cards: reading.cards,
    user_interpretation: reading.userInterpretation,
    ai_interpretation: reading.aiInterpretation,
    themes: reading.themes,
    advice: reading.advice,
    notes: reading.notes,
    image_url: reading.imageUrl,
    reading_title: reading.readingTitle,
    is_virtual: reading.is_virtual || false,
    user_id: reading.user_id
  };
}

export const generateInterpretation = async (
  spreadType: string,
  cards: Array<{ name: string; position: string; isReversed: boolean }>,
  context: string,
  userInterpretation: string = '',
  readingTitle?: string
) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-interpretation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        spreadType,
        cards,
        context,
        userInterpretation,
        readingTitle
      })
    });

    if (!response.ok) {
      throw new Error('Failed to generate interpretation');
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating interpretation:', error);
    throw error;
  }
};

export const saveReading = async (reading: Omit<Reading, 'id' | 'createdAt'>): Promise<Reading | null> => {
  console.time('🔁 Total saveReading duration');
  console.log('💾 Starting to save reading...');
  try {
    const { user } = useAuthStore.getState();
    if (!user) {
      throw new Error('User must be logged in to save readings');
    }

    // Validate required fields
    if (!reading.subject || !reading.context || !reading.spreadType) {
      throw new Error('Missing required reading fields');
    }

    // Validate and trim text fields to prevent oversized payloads
    const trimmedReading = {
      ...reading,
      subject: reading.subject.slice(0, 255),
      context: reading.context.slice(0, 2000),
      question: (reading.question || '').slice(0, 500),
      userInterpretation: (reading.userInterpretation || '').slice(0, 5000),
      aiInterpretation: reading.aiInterpretation.slice(0, 10000),
      advice: reading.advice.slice(0, 5000),
      notes: (reading.notes || '').slice(0, 5000)
    };

    // Validate cards array
    if (!Array.isArray(trimmedReading.cards) || trimmedReading.cards.length === 0) {
      throw new Error('Invalid cards data');
    }

    // Validate themes array
    if (!Array.isArray(trimmedReading.themes)) {
      trimmedReading.themes = [];
    }

    const newReading = {
      id: uuidv4(),
      created_at: new Date().toISOString(),
      ...mapReadingToSupabase(trimmedReading)
    };

    // Log payload size for debugging
    const payloadSize = JSON.stringify(newReading).length;
    console.log('📦 Reading data size:', payloadSize);
    if (payloadSize > 100000) { // 100KB warning threshold
      console.warn('⚠️ Large payload detected:', payloadSize, 'bytes');
    }

    // Insert with timeout
    console.time('📥 Supabase insert');
    const insertPromise = supabase
      .from('readings')
      .insert([newReading]);

    const insertResult = await Promise.race([
      insertPromise,
      new Promise((_, reject) => 
        setTimeout(() => {
          console.timeEnd('📥 Supabase insert');
          reject(new Error(`Insert operation timed out after ${INSERT_TIMEOUT}ms`));
        }, INSERT_TIMEOUT)
      )
    ]);

    if (insertResult.error) {
      console.error('❌ Insert operation failed:', insertResult.error);
      throw insertResult.error;
    }

    // Verify the save with a select
    const { data, error: selectError } = await supabase
      .from('readings')
      .select('*')
      .eq('id', newReading.id)
      .single();

    if (selectError || !data) {
      throw new Error('Failed to verify saved reading');
    }

    console.log('✅ Successfully saved reading:', data.id);
    console.timeEnd('🔁 Total saveReading duration');
    return mapReadingFromSupabase(data);

  } catch (error) {
    console.error('❌ Error saving reading:', error);
    console.timeEnd('🔁 Total saveReading duration');
    throw error;
  }
};

export const getReadings = async (): Promise<Reading[]> => {
  try {
    const { data, error } = await supabase
      .from('readings')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(mapReadingFromSupabase);
  } catch (error) {
    console.error('Error fetching readings:', error);
    return [];
  }
};

export const getReading = async (id: string): Promise<Reading | null> => {
  try {
    const { data, error } = await supabase
      .from('readings')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    return data ? mapReadingFromSupabase(data) : null;
  } catch (error) {
    console.error(`Error fetching reading ${id}:`, error);
    return null;
  }
};

export const deleteReading = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('readings')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error(`Error deleting reading ${id}:`, error);
    return false;
  }
};

export const uploadImage = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string | null> => {
  try {
    const { user } = useAuthStore.getState();
    if (!user) {
      throw new Error('Must be logged in to upload images');
    }

    // Additional validation
    if (!file.type.startsWith('image/')) {
      throw new Error('Invalid file type. Only images are allowed.');
    }

    // Size validation
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('Image size must be less than 10MB');
    }

    // Compress image if needed
    let imageFile = file;
    if (file.size > 2 * 1024 * 1024) { // Over 2MB
      console.log('Compressing large image...');
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
        onProgress
      };
      
      imageFile = await imageCompression(file, options);
      console.log('Image compressed', { newSize: imageFile.size });
    }

    // Create a unique filename with user ID prefix
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${user.id}/${uuidv4()}.${fileExt}`;
    const filePath = `reading-images/${fileName}`;

    // Report 50% progress before upload starts
    if (onProgress) {
      onProgress(50);
    }

    // Create upload timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Upload timeout exceeded')), UPLOAD_TIMEOUT);
    });

    // Create upload promise with auth headers
    const uploadPromise = supabase.storage
      .from('tarot-images')
      .upload(filePath, imageFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: imageFile.type
      });

    // Race between upload and timeout
    const { error: uploadError } = await Promise.race([
      uploadPromise,
      timeoutPromise
    ]) as any;

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    if (onProgress) {
      // Report 100% progress after successful upload
      onProgress(100);
    }

    // Get public URL after successful upload
    const { data } = supabase.storage
      .from('tarot-images')
      .getPublicUrl(filePath);

    if (!data?.publicUrl) {
      throw new Error('Failed to get public URL for uploaded image');
    }

    console.log('Upload completed successfully');
    return data.publicUrl;
  } catch (error) {
    console.error('Error in uploadImage:', error);
    throw error;
  }
};